import React from 'react';
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// #137 光泄漏过渡效果 (Light-leak Transition)
export const LightLeakTransition: React.FC<{
  text?: string;
  color1?: string;
  color2?: string;
  backgroundColor?: string;
}> = ({
  text = 'LIGHT LEAK',
  color1 = '#ff6b35',
  color2 = '#ffd93d',
  backgroundColor = '#0a0a0a',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 光泄漏动画（从左到右）
  const leakProgress = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  // 光带移动
  const leakX = interpolate(leakProgress, [0, 1], [-200, 2200]);

  // 主文字淡入
  const textOpacity = interpolate(frame, [30, 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 文字缩放
  const textScale = spring({
    frame: frame - 30,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 光泄漏效果 */}
      <div
        style={{
          position: 'absolute',
          width: 400,
          height: '100%',
          left: leakX,
          background: `linear-gradient(90deg, transparent, ${color1}88, ${color2}aa, ${color1}88, transparent)`,
          filter: 'blur(30px)',
          transform: 'rotate(-15deg)',
          mixBlendMode: 'screen',
        }}
      />

      {/* 额外的光泄漏层 */}
      <div
        style={{
          position: 'absolute',
          width: 300,
          height: '100%',
          left: leakX + 200,
          background: `linear-gradient(90deg, transparent, ${color2}66, transparent)`,
          filter: 'blur(50px)',
          transform: 'rotate(-15deg)',
          mixBlendMode: 'screen',
        }}
      />

      {/* 主文字 */}
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          color: '#ffffff',
          opacity: textOpacity,
          transform: `scale(${textScale})`,
          textShadow: `0 0 30px ${color1}, 0 0 60px ${color2}`,
          fontFamily: 'Arial, sans-serif',
          letterSpacing: 10,
        }}
      >
        {text}
      </div>

      {/* 光晕装饰 */}
      <div
        style={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color1}44, transparent)`,
          top: '10%',
          right: '10%',
          animation: `pulse ${fps / 4}s ease-in-out infinite`,
        }}
      />
    </AbsoluteFill>
  );
};

// #138 文字扭曲效果 (Text Warping)
export const TextWarping: React.FC<{
  text?: string;
  color?: string;
  backgroundColor?: string;
  warpIntensity?: number;
}> = ({
  text = 'WARP',
  color = '#00ffff',
  backgroundColor = '#0a0a1a',
  warpIntensity = 30,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const letters = text.split('');

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 背景波浪 */}
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '100%',
            height: 3,
            top: `${20 + i * 15}%`,
            background: `linear-gradient(90deg, transparent, ${color}22, transparent)`,
            transform: `translateY(${Math.sin((frame / 30) + i) * 20}px)`,
          }}
        />
      ))}

      {/* 扭曲文字 */}
      <div
        style={{
          display: 'flex',
          fontSize: 140,
          fontWeight: 'bold',
          color,
          fontFamily: 'Arial, sans-serif',
          textShadow: `0 0 20px ${color}`,
        }}
      >
        {letters.map((letter, i) => {
          // 每个字母不同的扭曲效果
          const warpX = Math.sin((frame / 20) + i * 0.8) * warpIntensity;
          const warpY = Math.cos((frame / 25) + i * 0.5) * warpIntensity * 0.5;
          const rotation = Math.sin((frame / 30) + i) * 15;
          const scale = 1 + Math.sin((frame / 15) + i) * 0.2;

          // 颜色偏移
          const hueShift = (frame * 2 + i * 30) % 360;
          const letterColor = `hsl(${hueShift}, 100%, 60%)`;

          return (
            <div
              key={i}
              style={{
                display: 'inline-block',
                transform: `translate(${warpX}px, ${warpY}px) rotate(${rotation}deg) scale(${scale})`,
                color: letterColor,
                textShadow: `0 0 30px ${letterColor}`,
              }}
            >
              {letter}
            </div>
          );
        })}
      </div>

      {/* 装饰粒子 */}
      {[...Array(20)].map((_, i) => {
        const angle = (i / 20) * Math.PI * 2;
        const radius = 300 + Math.sin(frame / 10 + i) * 50;
        const x = Math.cos(angle + frame / 50) * radius;
        const y = Math.sin(angle + frame / 50) * radius;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: color,
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${y}px)`,
              opacity: 0.5,
              boxShadow: `0 0 10px ${color}`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

// #139 苹果弹簧动画卡片 (Spring Physics Card)
export const SpringPhysicsCard: React.FC<{
  title?: string;
  subtitle?: string;
  cardColor?: string;
  backgroundColor?: string;
  icon?: string;
}> = ({
  title = 'Spring Physics',
  subtitle = 'Apple-style animation',
  cardColor = '#ffffff',
  backgroundColor = '#1e1e2e',
  icon = '🍎',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 弹簧动画（卡片进入）
  const cardSpring = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 100, mass: 0.8 },
  });

  // 卡片缩放和Y轴位移
  const cardScale = interpolate(cardSpring, [0, 1], [0.3, 1]);
  const cardY = interpolate(cardSpring, [0, 1], [100, 0]);

  // 图标弹跳
  const iconSpring = spring({
    frame: frame - 15,
    fps,
    config: { damping: 8, stiffness: 200 },
  });
  const iconScale = iconSpring;
  const iconRotation = interpolate(iconSpring, [0, 1], [-180, 0]);

  // 标题淡入
  const titleOpacity = interpolate(frame, [30, 50], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 副标题淡入
  const subtitleOpacity = interpolate(frame, [45, 65], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 阴影动画
  const shadowBlur = interpolate(cardSpring, [0, 1], [0, 40]);
  const shadowOpacity = interpolate(cardSpring, [0, 1], [0, 0.3]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 卡片 */}
      <div
        style={{
          width: 600,
          height: 400,
          backgroundColor: cardColor,
          borderRadius: 30,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transform: `scale(${cardScale}) translateY(${cardY}px)`,
          boxShadow: `0 ${shadowBlur}px ${shadowBlur * 2}px rgba(0, 0, 0, ${shadowOpacity})`,
          padding: 40,
        }}
      >
        {/* 图标 */}
        <div
          style={{
            fontSize: 100,
            marginBottom: 30,
            transform: `scale(${iconScale}) rotate(${iconRotation}deg)`,
          }}
        >
          {icon}
        </div>

        {/* 标题 */}
        <div
          style={{
            fontSize: 48,
            fontWeight: 'bold',
            color: '#1e1e2e',
            opacity: titleOpacity,
            marginBottom: 20,
            fontFamily: 'Arial, sans-serif',
          }}
        >
          {title}
        </div>

        {/* 副标题 */}
        <div
          style={{
            fontSize: 24,
            color: '#666',
            opacity: subtitleOpacity,
            fontFamily: 'Arial, sans-serif',
          }}
        >
          {subtitle}
        </div>

        {/* 进度条 */}
        <div
          style={{
            width: '100%',
            height: 6,
            backgroundColor: '#e0e0e0',
            borderRadius: 3,
            marginTop: 40,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${cardSpring * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #007AFF, #00D9FF)',
              borderRadius: 3,
            }}
          />
        </div>
      </div>

      {/* 装饰圆圈 */}
      {[...Array(3)].map((_, i) => {
        const delay = i * 10;
        const circleSpring = spring({
          frame: frame - delay,
          fps,
          config: { damping: 15, stiffness: 100 },
        });

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 100 + i * 50,
              height: 100 + i * 50,
              borderRadius: '50%',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              opacity: circleSpring * 0.3,
              top: `${10 + i * 20}%`,
              left: `${10 + i * 15}%`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};
